import { FunctionComponent, useMemo, memo } from 'react';
import { DoubleSide, SRGBColorSpace, TextureLoader } from 'three';
import { IImageReactProps, ITuple3, ITuple4 } from '../component-data-structure';
import { useTextureLoader } from '../hooks/useTextureLoader';
import { IUserData } from '../utils/general';
import CurvedEntity from './CurvedEntity/CurvedEntity';
import CustomBillboard from './CustomBillboard/CustomBillboard';

const Image: FunctionComponent<IImageReactProps> = ({
	scale,
	imageUrl,
	opacity = 1,
	hasAlpha = false,
	position,
	rotation,
	onPointerUp,
	onPointerMove,
	onPointerDown,
	onDoubleClick,
    onPointerOut,
    onPointerOver,
	renderOrder,
	borderRadius: radius = 0,
	borderRgba: brgba = [255, 0, 0, 1],
	borderWidth: bw = 0,
	curvature,
	id = '',
	raycast,
	castShadow = false,
	receiveShadow = false,
	animationTransformGroupPrefix = '',
    depthTest = true,
    side,
    billboard,
    isCameraOrbiting = false,
	rotateNoBillboardOuterGroup = false,
}) => {
	const texture = useTextureLoader(TextureLoader, imageUrl, true)
	if (typeof texture !== 'undefined') texture.colorSpace = SRGBColorSpace;
	const zeroPos = useMemo(() => [0, 0, 0] as ITuple3, []);
	const userData: IUserData = useMemo(() => ({ renderOrder, contentId: id, depthTest }), [id, renderOrder, depthTest]);

	return (
		<CustomBillboard
			key={'image_group'}
			userData={userData}
			raycast={raycast}
			name={`${animationTransformGroupPrefix}${id}`}
			position={position as ITuple3}
            rotation={rotation as ITuple3}
			onPointerUp={onPointerUp || undefined}
			onPointerDown={onPointerDown || undefined}
			onPointerMove={onPointerMove || undefined}
			onDoubleClick={onDoubleClick || undefined}
            onPointerOut={onPointerOut || undefined}
            onPointerOver={onPointerOver || undefined}
			follow={billboard?.[0]}
			lockY={!billboard?.[1]}
            isCameraOrbiting={isCameraOrbiting}
			rotateNoBillboardOuterGroup={rotateNoBillboardOuterGroup}
		>
            <group name={`${animationTransformGroupPrefix}inner_${id}`}>
                <CurvedEntity
                    raycast={raycast}
                    key={'img_bkgr'}
                    cornerRadius={radius}
                    opacity={opacity}
                    position={zeroPos as ITuple3}
                    renderOrder={renderOrder}
                    border={{
                        borderSize: bw,
                        borderRgba: brgba as ITuple4,
                    }}
                    castShadow={castShadow}
                    side={side ?? DoubleSide}
                    receiveShadow={receiveShadow}
                    imageTexture={texture}
                    scale={scale as ITuple3}
                    curvature={curvature}
                    hasAlpha={hasAlpha}
                    depthTest={depthTest}
                />
            </group>
		</CustomBillboard>
	);
};

Image.displayName = 'Image';
export default memo(Image);
